/**
 * OP76 — Cart exit feedback popup.
 *
 * Shows a short "why are you leaving?" survey when a shopper with items in the
 * cart abandons the cart page or closes the cart drawer. Submissions go to
 * Klaviyo via the public client endpoints (no private key, no backend), which
 * fires the flow that emails the discount code.
 */
(function () {
  'use strict';

  const root = document.getElementById('op76-cxp');
  const configEl = document.getElementById('op76-cxp-config');
  if (!root || !configEl) return;

  let cfg;
  try {
    cfg = JSON.parse(configEl.textContent);
  } catch (e) {
    return;
  }
  if (!cfg.companyId) return;

  const SHOWN_KEY = 'op76:cxp:shown';
  const ARMED_KEY = 'op76:cxp:armed';
  const DONE_KEY = 'op76:cxp:done';
  const KLAVIYO_REVISION = '2024-10-15';

  const form = root.querySelector('[data-cxp-form]');
  const successEl = root.querySelector('[data-cxp-success]');
  const errorEl = root.querySelector('[data-cxp-error]');
  const submitBtn = root.querySelector('[data-cxp-submit]');
  const emailField = root.querySelector('[data-cxp-email]');

  let cart = null;
  let isOpen = false;
  let headingForCheckout = false;
  let lastFocused = null;
  let trigger = '';
  let idleTimer = null;

  /* ----------------------------------------------------------------
     Cart state — kept warm so the exit trigger never waits on a fetch
  ---------------------------------------------------------------- */

  function refreshCart() {
    const previousCount = cart ? cart.item_count : null;
    return fetch(window.Shopify.routes.root + 'cart.js')
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        cart = data;
        // Adding something means they are still shopping, not leaving.
        if (data && previousCount !== null && data.item_count > previousCount) {
          disarm();
        }
      })
      .catch(() => {});
  }

  refreshCart();
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, refreshCart);
  }

  /* ----------------------------------------------------------------
     Eligibility
  ---------------------------------------------------------------- */

  function isArmed() {
    try {
      return sessionStorage.getItem(ARMED_KEY) === '1';
    } catch (e) {
      return false;
    }
  }

  function arm() {
    try {
      sessionStorage.setItem(ARMED_KEY, '1');
    } catch (e) {}
  }

  function disarm() {
    try {
      sessionStorage.removeItem(ARMED_KEY);
    } catch (e) {}
    clearTimeout(idleTimer);
    idleTimer = null;
  }

  function alreadyDone() {
    try {
      return localStorage.getItem(DONE_KEY) === '1';
    } catch (e) {
      return false;
    }
  }

  function alreadyShown() {
    try {
      return sessionStorage.getItem(SHOWN_KEY) === '1';
    } catch (e) {
      return false;
    }
  }

  function markShown() {
    try {
      sessionStorage.setItem(SHOWN_KEY, '1');
    } catch (e) {}
  }

  function markDone() {
    try {
      localStorage.setItem(DONE_KEY, '1');
    } catch (e) {}
  }

  function eligible() {
    if (isOpen || headingForCheckout) return false;
    if (alreadyDone() || alreadyShown()) return false;
    if (!cart || !cart.item_count) return false;
    // Away from the cart page we only qualify shoppers who opened the cart
    // drawer and backed out of it.
    if (!cfg.isCartPage && !isArmed()) return false;
    if (cfg.minCartValue && cart.total_price < cfg.minCartValue) return false;
    if (/\/(checkouts|orders|account)\//.test(window.location.pathname)) return false;
    return true;
  }

  /* ----------------------------------------------------------------
     Open / close
  ---------------------------------------------------------------- */

  function open(source) {
    if (!eligible()) return;
    isOpen = true;
    trigger = source || 'Unknown';
    markShown();
    lastFocused = document.activeElement;
    root.hidden = false;
    // Two frames so the browser paints the hidden state before transitioning.
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        root.classList.add('is-open');
      });
    });
    document.body.classList.add('op76-cxp-locked');

    const first = root.querySelector('input, textarea, button');
    if (first) first.focus({ preventScroll: true });

    track('Cart Exit Feedback Shown', { source: trigger });
  }

  function close() {
    if (!isOpen) return;
    isOpen = false;
    root.classList.remove('is-open');
    document.body.classList.remove('op76-cxp-locked');
    const done = () => {
      root.hidden = true;
    };
    root.addEventListener('transitionend', done, { once: true });
    setTimeout(done, 400);
    if (lastFocused && lastFocused.focus) lastFocused.focus({ preventScroll: true });
  }

  root.querySelectorAll('[data-cxp-close]').forEach((el) => {
    el.addEventListener('click', close);
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && isOpen) close();
  });

  // Trap focus inside the dialog while it is open.
  root.addEventListener('keydown', (e) => {
    if (e.key !== 'Tab' || !isOpen) return;
    const focusables = root.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled])'
    );
    if (!focusables.length) return;
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  });

  /* ----------------------------------------------------------------
     Triggers
  ---------------------------------------------------------------- */

  // Never interrupt someone on their way to checkout.
  document.addEventListener(
    'click',
    (e) => {
      const el = e.target.closest(
        '[name="checkout"], [href*="/checkout"], form[action*="/checkout"] button[type="submit"], .shopify-payment-button button'
      );
      if (el) {
        headingForCheckout = true;
        setTimeout(() => {
          headingForCheckout = false;
        }, 5000);
      }
    },
    true
  );

  // Exit intent, site-wide. eligible() decides whether this shopper qualifies:
  // on the cart page always, elsewhere only once the drawer has armed it.
  document.addEventListener('mouseout', (e) => {
    if (e.clientY > 0 || e.relatedTarget) return;
    if (e.target && e.target.closest && e.target.closest('#op76-cxp')) return;
    open(cfg.isCartPage ? 'Cart page' : 'Site — after cart drawer');
  });

  // Mobile back intent, cart page only.
  if (cfg.isCartPage && cfg.mobileBackIntent && window.matchMedia('(hover: none)').matches) {
    let trapped = false;
    const armHistory = () => {
      if (trapped) return;
      trapped = true;
      history.pushState({ op76cxp: true }, '', window.location.href);
    };
    setTimeout(armHistory, 3000);
    window.addEventListener('popstate', () => {
      if (!trapped) return;
      trapped = false;
      if (eligible()) {
        open('Cart page — back intent');
        armHistory();
      }
    });
  }

  /* ----------------------------------------------------------------
     Idle watch — the mobile answer, since phones have no exit intent
  ---------------------------------------------------------------- */

  function startIdleWatch() {
    clearTimeout(idleTimer);
    idleTimer = setTimeout(function () {
      open('Cart drawer — went idle');
    }, (cfg.idleSeconds || 45) * 1000);
  }

  function bumpIdleWatch() {
    // Only reschedule while a watch is actually pending.
    if (idleTimer) startIdleWatch();
  }

  ['pointerdown', 'keydown', 'scroll', 'touchstart'].forEach(function (evt) {
    document.addEventListener(evt, bumpIdleWatch, { passive: true });
  });

  /* ----------------------------------------------------------------
     Cart drawer — a qualifier, not a trigger
  ---------------------------------------------------------------- */

  function onDrawerClosed() {
    if (headingForCheckout) return; // they clicked through to checkout

    switch (cfg.drawerMode) {
      case 'immediate':
        setTimeout(function () {
          open('Cart drawer');
        }, cfg.drawerDelay || 500);
        break;

      case 'idle':
        arm();
        startIdleWatch();
        break;

      case 'exit':
        // Arm only. The popup waits for genuine exit intent.
        arm();
        break;

      default:
        break; // 'off'
    }
  }

  function hookDrawer() {
    if (typeof CartDrawer === 'undefined' || !CartDrawer.prototype) return false;
    const originalClose = CartDrawer.prototype.close;
    CartDrawer.prototype.close = function () {
      const result = originalClose.apply(this, arguments);
      // Let the checkout-click flag settle before deciding.
      setTimeout(onDrawerClosed, 50);
      return result;
    };
    return true;
  }

  if (!hookDrawer()) {
    // cart-drawer.js is deferred; retry once the document is parsed.
    document.addEventListener('DOMContentLoaded', hookDrawer);
  }

  // An armed shopper who navigates to a new page keeps their idle watch.
  if (cfg.drawerMode === 'idle' && isArmed()) startIdleWatch();

  /* ----------------------------------------------------------------
     Klaviyo
  ---------------------------------------------------------------- */

  function klaviyoPost(path, payload) {
    return fetch('https://a.klaviyo.com/client/' + path + '/?company_id=' + encodeURIComponent(cfg.companyId), {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
        revision: KLAVIYO_REVISION,
      },
      body: JSON.stringify(payload),
    }).then(function (response) {
      if (!response.ok) throw new Error('Klaviyo ' + path + ' responded ' + response.status);
      return response;
    });
  }

  function cartSnapshot() {
    if (!cart) return {};
    return {
      cart_value: cart.total_price / 100,
      cart_currency: cart.currency,
      cart_item_count: cart.item_count,
      cart_items: (cart.items || []).map((i) => ({
        title: i.product_title,
        variant: i.variant_title,
        quantity: i.quantity,
        price: i.final_price / 100,
      })),
      cart_url: window.location.origin + '/cart',
    };
  }

  function track(metric, properties, email) {
    const attributes = {
      metric: { data: { type: 'metric', attributes: { name: metric } } },
      properties: Object.assign({}, cartSnapshot(), properties),
      value: cart ? cart.total_price / 100 : undefined,
    };

    const profileAttrs = {};
    if (email) profileAttrs.email = email;
    else if (cfg.customerEmail) profileAttrs.email = cfg.customerEmail;

    // An anonymous shopper has no identifier yet — skip the event rather than
    // creating a junk profile.
    if (!profileAttrs.email) {
      if (window.klaviyo && typeof window.klaviyo.push === 'function') {
        window.klaviyo.push(['track', metric, attributes.properties]);
      }
      return Promise.resolve();
    }

    attributes.profile = { data: { type: 'profile', attributes: profileAttrs } };
    return klaviyoPost('events', { data: { type: 'event', attributes: attributes } });
  }

  function subscribeProfile(email, properties) {
    return klaviyoPost('subscriptions', {
      data: {
        type: 'subscription',
        attributes: {
          custom_source: 'Cart Exit Feedback',
          profile: {
            data: {
              type: 'profile',
              attributes: { email: email, properties: properties },
            },
          },
        },
        relationships: { list: { data: { type: 'list', id: cfg.listId } } },
      },
    });
  }

  /* ----------------------------------------------------------------
     Submit
  ---------------------------------------------------------------- */

  form.addEventListener('submit', function (e) {
    e.preventDefault();
    if (form.querySelector('[data-cxp-hp]').value) return; // honeypot

    const data = new FormData(form);
    const email = (data.get('email') || '').trim();
    const reason = data.get('reason') || '';
    const comment = (data.get('comment') || '').trim();

    errorEl.hidden = true;

    if (!reason) {
      showError(cfg.text.errorReason);
      return;
    }

    // Email is optional — validate only what was actually typed.
    if (email && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
      showError(cfg.text.errorEmail);
      emailField.focus();
      return;
    }

    submitBtn.disabled = true;
    submitBtn.classList.add('is-loading');

    const properties = {
      reason: reason,
      comment: comment,
      source: trigger,
      gave_email: Boolean(email),
    };

    // With an email we subscribe first so the flow has a profile to send to.
    // Without one, the feedback still gets recorded against the session.
    const request = email
      ? subscribeProfile(email, { $source: 'Cart Exit Feedback' }).then(function () {
          return track('Cart Exit Feedback', properties, email);
        })
      : track('Cart Exit Feedback', properties);

    request
      .then(function () {
        markDone();
        showSuccess(Boolean(email));
      })
      .catch(function () {
        showError(cfg.text.errorGeneric);
      })
      .finally(function () {
        submitBtn.disabled = false;
        submitBtn.classList.remove('is-loading');
      });
  });

  function showSuccess(hasEmail) {
    const body = root.querySelector('[data-cxp-success-body]');
    if (body) body.innerHTML = hasEmail ? cfg.text.successWithEmail : cfg.text.successNoEmail;

    // The code is only meaningful if we can also email it to them.
    const code = root.querySelector('[data-cxp-code]');
    if (code) code.hidden = !hasEmail;

    form.hidden = true;
    successEl.hidden = false;
    successEl.focus({ preventScroll: true });
  }

  function showError(message) {
    errorEl.textContent = message;
    errorEl.hidden = false;
  }

  // Copy-to-clipboard on the discount code in the success state.
  const codeBtn = root.querySelector('[data-cxp-code]');
  if (codeBtn) {
    codeBtn.addEventListener('click', function () {
      navigator.clipboard.writeText(codeBtn.dataset.cxpCode).then(() => {
        codeBtn.classList.add('is-copied');
        setTimeout(() => codeBtn.classList.remove('is-copied'), 2000);
      });
    });
  }

  // Debug hatch: op76CartExitPopup.open() from the console, ignoring guards.
  window.op76CartExitPopup = {
    open: function () {
      try {
        sessionStorage.removeItem(SHOWN_KEY);
        localStorage.removeItem(DONE_KEY);
      } catch (e) {}
      if (!cart) cart = { item_count: 1, total_price: 0, items: [] };
      arm();
      open('Manual');
    },
    close: close,
  };
})();
